import { useState, useCallback, useRef } from "react";
import type { ChatMessage, SSEEvent } from "../types";
import { getChatApiEndpoint, MAX_HISTORY_MESSAGES, WELCOME_MESSAGE, ASSISTANT_NAME } from "../config";

/** Generates a simple unique id for messages. */
function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Persistent session id for the browser tab. */
function getSessionId(): string {
  let id = sessionStorage.getItem("mc-chat-session");
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem("mc-chat-session", id);
  }
  return id;
}

const WELCOME: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content: WELCOME_MESSAGE,
  timestamp: new Date().toISOString(),
};

interface UseChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  toolActivity: string | null;
  sendMessage: (text: string) => Promise<void>;
  reset: () => void;
}

/**
 * Core chat hook. Manages message history and SSE streaming from /api/chat.
 *
 * Dev fallback: if /api/chat returns a non-ok status or is unreachable in
 * local dev without Netlify Dev running, the hook falls back to a mock response
 * so the UI is still exercisable.
 */
export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolActivity, setToolActivity] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sessionId = useRef(getSessionId());

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;

    // Add user message
    const userMsg: ChatMessage = {
      id: uid(),
      role: "user",
      content: text.trim(),
      timestamp: new Date().toISOString(),
    };

    // Build the assistant placeholder message that will be streamed into
    const assistantMsgId = uid();
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);
    setToolActivity(null);

    // Build history to send (exclude welcome message, cap at MAX_HISTORY_MESSAGES)
    const historyToSend = [...messages, userMsg]
      .filter((m) => m.id !== "welcome")
      .slice(-MAX_HISTORY_MESSAGES)
      .map(({ role, content }) => ({ role, content }));

    abortRef.current = new AbortController();

    try {
      const response = await fetch(getChatApiEndpoint(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: historyToSend, sessionId: sessionId.current }),
        signal: abortRef.current.signal,
      });

      if (!response.ok || !response.body) {
        // Dev fallback when the edge function isn't running
        await mockStreamFallback(assistantMsgId, setMessages);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE: each event is "data: <json>\n\n"
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const raw = part.slice(6).trim();
          if (raw === "[DONE]") continue;

          try {
            const event: SSEEvent = JSON.parse(raw);
            handleSSEEvent(event, assistantMsgId, setMessages, setToolActivity);
          } catch {
            // malformed event, skip
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") return;
      // Network error fallback
      await mockStreamFallback(assistantMsgId, setMessages);
    } finally {
      setIsStreaming(false);
      setToolActivity(null);
    }
  }, [messages, isStreaming]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([WELCOME]);
    setIsStreaming(false);
    setToolActivity(null);
    sessionStorage.removeItem("mc-chat-session");
    sessionId.current = getSessionId();
  }, []);

  return { messages, isStreaming, toolActivity, sendMessage, reset };
}

/** Applies a single SSE event to message state. */
function handleSSEEvent(
  event: SSEEvent,
  assistantMsgId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  setToolActivity: React.Dispatch<React.SetStateAction<string | null>>,
) {
  switch (event.type) {
    case "delta":
      if (event.text) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsgId
              ? { ...m, content: m.content + event.text }
              : m,
          ),
        );
      }
      break;
    case "tool_start":
      setToolActivity(event.tool ?? "Searching...");
      break;
    case "tool_end":
      setToolActivity(null);
      break;
    case "error":
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                content:
                  m.content ||
                  `Sorry, something went wrong. Please try again. (${event.message ?? "unknown error"})`,
              }
            : m,
        ),
      );
      break;
    case "done":
    default:
      break;
  }
}

/**
 * Mock stream fallback for local dev without the Edge Function running.
 * Simulates a streamed response so the UI is exercisable.
 */
async function mockStreamFallback(
  assistantMsgId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
) {
  const mockText = `Hi! I'm ${ASSISTANT_NAME} (running in **dev mock mode** because the Edge Function isn't available locally right now).

To see real responses, start [Netlify Dev](https://docs.netlify.com/cli/local-development/) with \`netlify dev\` in the project root, which will proxy \`/api/chat\` to the edge function.

In the meantime, here's what I'd normally do:
- Ask you a few questions about how you sleep
- Search the Mattress Crafters product catalogue
- Recommend a mattress with a real product link

What kind of sleeper are you?`;

  // Stream character by character with a small delay to simulate real streaming
  for (const char of mockText) {
    await new Promise((r) => setTimeout(r, 12));
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantMsgId ? { ...m, content: m.content + char } : m,
      ),
    );
  }
}
